/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useState, useEffect } from 'react';
import { FormControl, FormControlLabel, Grid, Switch, Toolbar, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectTargetDate,
  selectVisualFilters,
  selectIsSignedIn,
  selectPmsSync,
  selectHotelName,
  selectHotelDropdownList,
  selectHotelGroupDropdownList,
  selectMergedHotelList,
  selectPredefinedHotelGroups,
  selectIsDemoModeActivated,
  selectResetBookingDate,
} from 'modules/dashboard/selectors';
import { getComparisonDateRange } from 'modules/dashboard/functions';
import { CustomStack, CustomToggleSwitch } from 'modules/common/components';
import { capitalizeFirst } from 'modules/common/helpers/strings';
import {
  OTB_VALUE,
  KEYS,
  STAY_DATE,
  ALIGNED,
  WORKBOOK_DATE_SELECTION,
} from 'modules/dashboard/constants';
import ActionButton from 'modules/common/components/action-button';
import styles from 'modules/dashboard/components/tab-container/tab-container.module.scss';
import DataUpdatedLabel from 'modules/dashboard/components/tab-container/data-updated-label';
import BookingWindow from 'modules/dashboard/components/tab-container/inner-filter/components/booking-window';
import VsComparisonWindow from 'modules/dashboard/components/tab-container/inner-filter/components/vs-comparison-window';
import {
  getDateComparisonFilter,
  getSubAlignmentFilter,
} from 'modules/dashboard/components/tab-container/inner-filter/functions';
import { dashboardActions } from 'modules/dashboard/slice';
import {
  selectAlignment,
  selectDateComparison,
  selectWorkbookCustomDate,
  selectWorkbookEndDate,
  selectWorkbookStartDate,
  selectSymmetricComparisonToggle,
  selectWorkbookAlignmentToggle,
  selectWorkbookOtb,
  selectSymmetricComparisonIsDisplay,
  selectHotelFocusOn,
  selectEnableSecondaryHotelGroups,
  selectNormalizedView,
  selectWorkbookHotelComparison,
  selectIsRatePlan,
  selectD1SetIsDisplay,
  selectD2SetIsDisplay,
  selectD3SetIsDisplay,
  selectOriginalHotelFocusOn,
  selectIsDetailed,
  selectIsTravelAgent,
  selectIsSecondaryHotelSetOverTimeDisabled,
  selectIsSecondaryHotelSetOverTime,
  selectInvalidSecondaryHotelList,
  selectIsRoomNightsPace,
  selectIsDashboardLeapComparison,
  selectIsRoomType,
} from '../../selectors';
import { workbookActions } from '../../slice';
import useWorkbookCommandPipeline from '../../hooks/use-workbook-command-pipeline';
import { CompareHotelsWindow, PmsToggle } from '../../../inner-filter';
import { ENVIRONMENT } from '../../../../../../../config';

/**
 * Filters for Workbook widget include PMS Sync, Booking Dates, Chart Breakdown,
 * Alignment, Date Comparison, Pace Horizon, pace variable and pace forecast
 * @param {Number} tabValue - Pace widget tab value
 * @param {Date} latestDate - Latest booking date of selected hotel
 * @param {Boolean} hotelTimezone - Timezone of selected hotel
 * @param {Number} toggleDisabled - PMS sync toggle disabled state
 * @param {Boolean} isLoaded - Visual is loaded state
 * @param {String} reportType - Report type of active tab
 * @param {Object} visual- Symmetric comparison visual object
 * @returns
 */
const WorkBookFilter = ({
  tabValue,
  reportType,
  visual,
  visualName,
  latestDate,
  hotelTimezone,
  isLoaded,
  toggleDisabled,
}) => {
  const dispatch = useDispatch();
  // Selected filter values are obtained from redux store. Refer each selector function for details.
  const alignment = useSelector(selectAlignment);
  const dateComparison = useSelector(selectDateComparison);
  const visualFilters = useSelector(selectVisualFilters);
  const targetDate = useSelector(selectTargetDate);
  const alignmentToggle = useSelector(selectWorkbookAlignmentToggle);
  const symmetricComparisonToggle = useSelector(selectSymmetricComparisonToggle);
  const isSignIn = useSelector(selectIsSignedIn);
  const customBooking = useSelector(selectWorkbookOtb);
  const customDate = useSelector(selectWorkbookCustomDate);
  const startDate = useSelector(selectWorkbookStartDate);
  const endDate = useSelector(selectWorkbookEndDate);
  const symmetricComparisonIsDisplay = useSelector(selectSymmetricComparisonIsDisplay);
  const pmsSyncToggle = useSelector(selectPmsSync);
  const hotelList = useSelector(selectHotelDropdownList);
  const hotelFocusOn = useSelector(selectHotelFocusOn);
  const enableSecondaryHotelGroups = useSelector(selectEnableSecondaryHotelGroups);
  const hotelGroupList = useSelector(selectHotelGroupDropdownList);
  const normalizedViewToggle = useSelector(selectNormalizedView);
  const mergedHotelList = useSelector(selectMergedHotelList);
  const isWorkbookHotelComparison = useSelector(selectWorkbookHotelComparison);
  const isRatePlan = useSelector(selectIsRatePlan);
  const predefinedHotelGroups = useSelector(selectPredefinedHotelGroups);
  const d1SetIsDisplay = useSelector(selectD1SetIsDisplay);
  const d2SetIsDisplay = useSelector(selectD2SetIsDisplay);
  const d3SetIsDisplay = useSelector(selectD3SetIsDisplay);
  const originalHotelFocusOn = useSelector(selectOriginalHotelFocusOn);
  const isDemoModeActivated = useSelector(selectIsDemoModeActivated);
  const isDetailed = useSelector(selectIsDetailed);
  const isTravelAgentOn = useSelector(selectIsTravelAgent);
  const isHotelSetOverTimeDisabled = useSelector(selectIsSecondaryHotelSetOverTimeDisabled);
  const hotelSetOverTime = useSelector(selectIsSecondaryHotelSetOverTime);
  const selectedHotelName = useSelector(selectHotelName);
  const invalidPrimaryHotels = useSelector(selectInvalidSecondaryHotelList);
  const isRoomNightsPace = useSelector(selectIsRoomNightsPace);
  const isRoomType = useSelector(selectIsRoomType);
  const resetBookingDate = useSelector(selectResetBookingDate);
  const hasLeap = useSelector(selectIsDashboardLeapComparison);
  //
  const [dateComparisonList, setDateComparisonList] = useState([]);
  const [disableNormalizedViewToggle, setDisableNormalizedViewToggle] = useState(true);

  // To select otb or custom booking window
  const handleOTBChange = (event) => {
    const { value } = event.target;
    dispatch(workbookActions.setWorkbookOtb(value !== 'false'));
    dispatch(workbookActions.setWorkbookBookingDate(resetBookingDate));
  };
  // Resets the display state of three visual elements
  const resetVisualState = (d1IsDisplay, d2IsDisplay, d3IsDisplay) => {
    setTimeout(() => {
      dispatch(workbookActions.setD1SetIsDisplay(d1IsDisplay));
      dispatch(workbookActions.setD2SetIsDisplay(d2IsDisplay));
      dispatch(workbookActions.setD3SetIsDisplay(d3IsDisplay));
    }, 1000);
  };
  // Reset button function to set filter panel values to default
  const clearSearch = () => {
    const d1IsDisplay = d1SetIsDisplay;
    const d2IsDisplay = d2SetIsDisplay;
    const d3IsDisplay = d3SetIsDisplay;
    if (d1SetIsDisplay) {
      dispatch(workbookActions.setD1SetIsDisplay(false));
    }
    if (d2SetIsDisplay) {
      dispatch(workbookActions.setD2SetIsDisplay(false));
    }
    if (d3SetIsDisplay) {
      dispatch(workbookActions.setD3SetIsDisplay(false));
    }
    dispatch(workbookActions.setWorkbookOtb(false));
    const subAlignmentFilter = getSubAlignmentFilter(visualFilters, false);
    dispatch(
      workbookActions.setDateComparison(
        subAlignmentFilter?.values?.filter((filter) => filter.isDefault === true)[0].value
      )
    );
    dispatch(workbookActions.setIsRatePlan(false));
    dispatch(workbookActions.setIsRoomType(false));
    dispatch(workbookActions.setIsRoomNightsPace(false));
    dispatch(workbookActions.setWorkbookBookingDate(resetBookingDate));
    dispatch(workbookActions.setWorkbookAlignmentToggle(subAlignmentFilter?.type === ALIGNED));
    dispatch(workbookActions.setSymmetricComparisonToggle(true));
    dispatch(workbookActions.setWorkbookStartDate(STAY_DATE.startDate));
    dispatch(workbookActions.setWorkbookEndDate(STAY_DATE.endDate));
    dispatch(workbookActions.setWorkbookCustomDate(KEYS.START_DATE));
    dispatch(workbookActions.setWorkbookDateSelected(STAY_DATE.startDate));
    dispatch(workbookActions.setNormalizedView(false));
    dispatch(workbookActions.setIsTravelAgent(false));
    dispatch(workbookActions.setIsDetailed(false));
    dispatch(dashboardActions.setWorkbookDateType(WORKBOOK_DATE_SELECTION[0]));
    //
    resetVisualState(d1IsDisplay, d2IsDisplay, d3IsDisplay);
  };

  // Triggered when alignment, visualFilters, alignmentToggle changes
  useEffect(() => {
    // Set date comparison list based on alignment
    if (alignment) {
      const dateComparisonFilter = getDateComparisonFilter(visualFilters, alignment?.id);
      setDateComparisonList(dateComparisonFilter);
      // persist value on browser refresh
      const serializedState = JSON.parse(
        localStorage.getItem(`persist:${ENVIRONMENT.PERSIST_KEY}`)
      );
      //
      if (!isSignIn && serializedState) {
        return;
      }
      dispatch(
        workbookActions.setDateComparison(
          dateComparisonFilter?.filter((filter) => filter?.isDefault === true)[0].value
        )
      );
    }
  }, [alignment, visualFilters, alignmentToggle]);

  // Triggered when alignmentToggle or visualFilters value changes
  useEffect(() => {
    // Set alignment list
    const subAlignmentFilter = getSubAlignmentFilter(visualFilters, alignmentToggle);
    dispatch(
      workbookActions.setAlignment({
        id: subAlignmentFilter?.id,
        label: capitalizeFirst(subAlignmentFilter?.type),
      })
    );
  }, [alignmentToggle, visualFilters]);

  // Alignment toggle change handler
  const handleToggleChange = (event) => {
    dispatch(workbookActions.setWorkbookAlignmentToggle(event.target.checked));
    const dateComparisonFilter = getDateComparisonFilter(visualFilters, alignment?.id);
    dispatch(
      workbookActions.setDateComparison(
        dateComparisonFilter?.filter((filter) => filter?.isDefault === true)[0].value
      )
    );
  };
  // Date comparison change handler
  const handleComparisonChange = (event) => {
    dispatch(workbookActions.setDateComparison(event.target.value));
    dispatch(workbookActions.setWorkbookDateSelected(STAY_DATE.startDate));
    dispatch(workbookActions.setWorkbookCustomDate(KEYS.START_DATE));
    //
    const dateRange = getComparisonDateRange(
      STAY_DATE.startDate,
      targetDate.startDate,
      targetDate.endDate,
      KEYS.START_DATE
    );
    //
    dispatch(workbookActions.setWorkbookStartDate(dateRange?.comparisonStartDate.toISOString()));
    dispatch(workbookActions.setWorkbookEndDate(dateRange?.comparisonEndDate.toISOString()));
  };

  // Triggered when customBooking, dateComparison, visualFilters, isLoaded change
  useEffect(() => {
    // Assign default value to date comparison when OTB is selected for booking window
    if (!customBooking && dateComparison === OTB_VALUE && isLoaded) {
      const subAlignmentFilter = getSubAlignmentFilter(visualFilters, true);
      dispatch(
        workbookActions.setDateComparison(
          subAlignmentFilter?.values?.filter((filter) => filter.isDefault === true)[0].value
        )
      );
    }
  }, [customBooking, dateComparison, visualFilters, isLoaded]);
  /**
   * set symmetric comparison value
   * @param {*} event - on change event
   */
  const handleSymmetricComparisonChange = (event) => {
    dispatch(workbookActions.setSymmetricComparisonToggle(event.target.checked));
  };
  /**
   * set custom dates value
   * @param {*} event - on change event
   */
  const handleCustomDates = (event) => {
    dispatch(workbookActions.setWorkbookCustomDate(event.target.value));
  };
  useEffect(() => {
    if (isRatePlan) {
      dispatch(workbookActions.setIsRoomNightsPace(false));
      dispatch(workbookActions.setIsTravelAgent(false));
      dispatch(workbookActions.setIsDetailed(false));
      dispatch(workbookActions.setNormalizedView(false));
      dispatch(workbookActions.setIsRoomType(false));
      setDisableNormalizedViewToggle(true);
    } else if (isWorkbookHotelComparison) {
      dispatch(workbookActions.setNormalizedView(false));
      setDisableNormalizedViewToggle(false);
    } else {
      dispatch(workbookActions.setNormalizedView(false));
      setDisableNormalizedViewToggle(true);
    }
  }, [isWorkbookHotelComparison, isRatePlan]);
  //
  useEffect(() => {
    if (isTravelAgentOn) {
      dispatch(workbookActions.setIsRoomNightsPace(false));
      dispatch(workbookActions.setIsRoomType(false));
      dispatch(workbookActions.setIsRatePlan(false));
      dispatch(workbookActions.setNormalizedView(false));
      setDisableNormalizedViewToggle(true);
    }
    if (!isTravelAgentOn) {
      dispatch(workbookActions.setIsDetailed(false));
    }
  }, [isTravelAgentOn]);
  //
  useEffect(() => {
    if (isRoomType) {
      dispatch(workbookActions.setIsRoomNightsPace(false));
      dispatch(workbookActions.setIsTravelAgent(false));
      dispatch(workbookActions.setIsRatePlan(false));
      dispatch(workbookActions.setNormalizedView(false));
      setDisableNormalizedViewToggle(true);
    }
    if (!isRoomType) {
      dispatch(workbookActions.setIsDetailed(false));
    }
  }, [isRoomType]);
  //
  useEffect(() => {
    if (isRoomNightsPace) {
      dispatch(workbookActions.setIsRatePlan(false));
      dispatch(workbookActions.setIsTravelAgent(false));
      dispatch(workbookActions.setIsDetailed(false));
      dispatch(workbookActions.setNormalizedView(false));
      dispatch(workbookActions.setIsRoomType(false));
      setDisableNormalizedViewToggle(true);
    } else if (isWorkbookHotelComparison) {
      dispatch(workbookActions.setNormalizedView(false));
      setDisableNormalizedViewToggle(false);
    } else {
      dispatch(workbookActions.setNormalizedView(false));
      setDisableNormalizedViewToggle(true);
    }
  }, [isWorkbookHotelComparison, isRoomNightsPace]);
  //
  const handleIsDetailedChange = (event) => {
    dispatch(workbookActions.setIsDetailed(event.target.checked));
  };
  //
  useWorkbookCommandPipeline(tabValue, {
    toggleDisabled,
    isLoaded,
    customBooking,
    dateComparison,
    dateComparisonList,
    hotelList,
    hotelGroupList: [...hotelGroupList, ...predefinedHotelGroups],
    handleComparisonChange,
    handleToggleChange,
  });
  //
  return (
    <>
      <PmsToggle
        disabled={toggleDisabled}
        value={pmsSyncToggle}
        onChange={(event) => dispatch(dashboardActions.setPmsSync(event.target.checked))}
        id="auto-pms-toggle"
      />
      <br />
      <BookingWindow
        vOTB={customBooking}
        customBooking={customBooking}
        handleOTBChange={handleOTBChange}
        reportType={reportType}
        latestDate={latestDate}
        otbValue={false}
        id="workbook"
      />
      <br />
      <CustomStack>
        <FormControl>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ color: '#0009' }}>Views</Typography>
            <FormControl>
              <FormControlLabel
                control={<Switch checked={isDetailed} onChange={handleIsDetailedChange} />}
                sx={{ '& .css-1cn7wjw-MuiTypography-root': { fontSize: 12 } }}
                label="Details"
                disabled={!(isTravelAgentOn || isRatePlan)}
              />
            </FormControl>
          </Grid>
        </FormControl>
        <CustomToggleSwitch
          label="Consortia / IATA"
          title=""
          onChange={(event) => {
            dispatch(workbookActions.setIsTravelAgent(event.target.checked));
            dispatch(workbookActions.setIsSecondaryHotelSetOverTime(false));
            dispatch(workbookActions.setIsSecondaryHotelSetOverTimeDisabled(event.target.checked));
          }}
          checked={isTravelAgentOn}
          className="consortia-iata-toggle"
        />
        <CustomToggleSwitch
          label="Rate Category / Rate Plan"
          title="Analyze rate plans with metrics comparing selected and comparison dates"
          onChange={(event) => {
            dispatch(workbookActions.setIsRatePlan(event.target.checked));
            dispatch(workbookActions.setIsSecondaryHotelSetOverTime(false));
            dispatch(workbookActions.setIsSecondaryHotelSetOverTimeDisabled(event.target.checked));
          }}
          checked={isRatePlan}
          className="rate-plan-toggle"
        />
        <CustomToggleSwitch
          label="Room Type"
          title=""
          onChange={(event) => {
            dispatch(workbookActions.setIsRoomType(event.target.checked));
            dispatch(workbookActions.setIsSecondaryHotelSetOverTime(false));
            dispatch(workbookActions.setIsSecondaryHotelSetOverTimeDisabled(event.target.checked));
          }}
          checked={isRoomType}
          className="room-type-toggle"
        />
        <CustomToggleSwitch
          label="Room Nights Pace (Monthly)"
          title=""
          onChange={(event) => {
            dispatch(workbookActions.setIsRoomNightsPace(event.target.checked));
            dispatch(workbookActions.setIsSecondaryHotelSetOverTime(false));
            dispatch(workbookActions.setIsSecondaryHotelSetOverTimeDisabled(event.target.checked));
          }}
          checked={isRoomNightsPace}
          className="room-night-pace-toggle"
        />
      </CustomStack>
      <br />
      <CompareHotelsWindow
        selectedHotelName={selectedHotelName}
        hotelList={hotelList ?? []}
        mergedHotelList={mergedHotelList ?? []}
        focusOn={hotelFocusOn ?? []}
        focusOnLabel={hotelFocusOn?.length ? 'Compared with' : 'Select Hotel'}
        handleCompareHotelsChange={(event, value) => {
          dispatch(workbookActions.setHotelFocusOn(value));
          dispatch(workbookActions.setIsSecondaryHotelSetOverTime(false));
        }}
        normalizedViewToggle={normalizedViewToggle}
        disableNormalizedViewToggle={disableNormalizedViewToggle}
        handleTrendHotelNormalize={(event) =>
          dispatch(workbookActions.setNormalizedView(event.target.checked))
        }
        enableSecondaryHotelGroups={enableSecondaryHotelGroups}
        hotelGroupList={hotelGroupList}
        id="workbook"
        isHotelComparison={isWorkbookHotelComparison}
        isRatePlanEnabled={isRatePlan}
        originalHotelFocusOn={originalHotelFocusOn}
        isDemoModeActivated={isDemoModeActivated}
        setOriginalHotelFocusOn={(focus) => {
          dispatch(workbookActions.setOriginalHotelFocusOn(focus));
        }}
        resetFocusOn={(focus) => {
          dispatch(workbookActions.resetHotelFocusOn(focus));
        }}
        isTravelAgentOn={isTravelAgentOn}
        hotelSetOverTime={hotelSetOverTime}
        handleHotelSetChange={(event) => {
          dispatch(workbookActions.setIsSecondaryHotelSetOverTime(event.target.checked));
          if (hotelFocusOn?.id === selectedHotelName?.id) {
            dispatch(dashboardActions.setIsPrimaryHotelSetOverTime(event.target.checked));
          }
        }}
        isHotelSetOverTimeDisabled={isHotelSetOverTimeDisabled}
        invalidPrimaryHotels={invalidPrimaryHotels}
        isRoomNightsPaceEnabled={isRoomNightsPace}
      />
      <br />
      <VsComparisonWindow
        customBooking={customBooking}
        alignmentToggle={alignmentToggle}
        handleToggleChange={handleToggleChange}
        symmetricComparisonToggle={symmetricComparisonToggle}
        visual={visual}
        visualName={visualName}
        dateComparison={dateComparison}
        dateComparisonList={dateComparisonList}
        handleComparisonChange={handleComparisonChange}
        handleSymmetricComparisonChange={handleSymmetricComparisonChange}
        isCustomOptionAvailable
        customDate={customDate}
        reportType={reportType}
        startDate={startDate}
        endDate={endDate}
        targetDate={targetDate}
        handleCustomDates={handleCustomDates}
        isDisplay={symmetricComparisonIsDisplay}
        isDisplayOnClickAction={() => {
          dispatch(workbookActions.setSymmetricComparisonIsDisplay(!symmetricComparisonIsDisplay));
        }}
        isTimeComparisonDisabled={isWorkbookHotelComparison && !isRatePlan && !isTravelAgentOn}
        hasLeap={hasLeap}
      />
      <br />
      <CustomStack cssClassName={styles.innerFilter}>
        <ActionButton onClick={clearSearch} label="RESET" />
      </CustomStack>
      <Toolbar />
      <DataUpdatedLabel
        latestDate={latestDate}
        hotelTimezone={hotelTimezone}
        toggleDisabled={toggleDisabled}
      />
    </>
  );
};
//
export default WorkBookFilter;
